import { useRouter } from 'next/router'

import { BootcampModuleCard, CategoryPathUrls } from '~/bootcamp/types/bootcamp.types'
import { CATEGORY_COLORS } from '~/bootcamp/constants/bootcamp.const'
import { BootcampOverviewUserData, bootcampOverviewGroq, bootcampOverviewModule } from '~/types/bootcamp'
import useProcessGymOverviewData from '~/bootcamp/hooks/useProcessGymOverviewData'
import bootcampAnalytics from '~/analytics/bootcamp/bootcampAnalytics'
import { BootcampEvents } from '~/analytics/bootcamp/bootcampEvents.types'
import { mathTopicsData } from '~/shared/shared.constants'

import BootcampCard from './BootcampCard'

function VideoCourseList({
  data,
  courses,
  bootcampKey,
  subtopic,
  className = '',
}: {
  data: bootcampOverviewGroq
  courses: BootcampOverviewUserData[]
  bootcampKey: string
  subtopic: string
  className?: string
}): React.ReactElement {
  const { locale, query } = useRouter()
  const { gradientClass, secondaryBgClass } = CATEGORY_COLORS[subtopic as CategoryPathUrls]
  const topicsIconData = mathTopicsData.find((topic) => topic.key === subtopic)
  const subjectListData = useProcessGymOverviewData(data, courses)

  const courseList: BootcampModuleCard[] = subjectListData.modules
    ? subjectListData.modules.map((subjectData: bootcampOverviewModule) => {
        const {
          name,
          moduleNumber,
          chests,
          progress,
          prerequisites,
          bossDefeated,
          badgeImage,
          badgeOutlineImage,
          hasWhyLevel,
          firstPageData,
          tagStatus,
          isUpcoming,
          taskReferences,
        } = subjectData

        return {
          moduleKey: moduleNumber,
          path: `/bootcamp/${subtopic}/${bootcampKey}/${moduleNumber}/1/${
            hasWhyLevel ? 'why' : firstPageData?.regular ? 'how' : 'definition'
          }`,
          progress,
          name,
          prerequisites,
          bossDefeated,
          badgeImage,
          badgeOutlineImage,
          chests,
          tagStatus,
          isUpcoming,
          taskReferences,
        }
      })
    : []

  const trackUpcomingBootcampClick = (moduleKey?: string): void => {
    bootcampAnalytics.log(BootcampEvents.CLICK_UPCOMING_BOOTCAMP, {
      locale,
      category: query.subtopic as string,
      subject: query.key as string,
      bootcamp: moduleKey,
    })
  }

  return (
    <div
      className={`${className} grid gap-2 sm:gap-4 pt-4 pb-4 sm:pb-8 md:py-0 mb-0 border-b md:border-0 border-purple-medium`}
    >
      {courseList.map(
        ({
          name,
          path,
          chests,
          progress,
          prerequisites,
          bossDefeated,
          badgeImage,
          badgeOutlineImage,
          tagStatus,
          isUpcoming,
          moduleKey,
          taskReferences,
        }) => (
          <BootcampCard
            badgeImage={badgeImage}
            badgeOutlineImage={badgeOutlineImage}
            bossDefeated={bossDefeated}
            chests={chests}
            isUpcoming={isUpcoming}
            key={path}
            moduleColor={gradientClass}
            name={name}
            onClick={isUpcoming ? () => trackUpcomingBootcampClick(moduleKey) : undefined}
            path={path}
            prerequisites={prerequisites}
            progress={progress}
            secondaryColor={secondaryBgClass}
            tagStatus={tagStatus}
            taskReferences={taskReferences}
            topicsIconData={topicsIconData}
          />
        ),
      )}
    </div>
  )
}

export default VideoCourseList
